<template>
  <div class="mx-10 ma-5">
    <v-card flat class="pa-5">
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.branch_name"
            :items="branches"
            item-text="name"
            item-value="name"
            label="Branch"
            clearable
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="filters.product_name"
            :items="products"
            :item-text="(product) => `${product.product_code} - ${product.name}`"
            item-value="product_code"
            label="Product"
            clearable
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="3">
          <v-menu
            ref="startMenu"
            v-model="startMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="filters.start_date"
                label="Start Date"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
              />
            </template>
            <v-date-picker
              v-model="filters.start_date"
              @input="startMenu = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="3">
          <v-menu
            ref="endMenu"
            v-model="endMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="filters.end_date"
                label="End Date"
                readonly
                v-bind="attrs"
                v-on="on"
                dense
              />
            </template>
            <v-date-picker
              v-model="filters.end_date"
              @input="endMenu = false"
            />
          </v-menu>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="2">
          <v-btn color="primary" @click="fetchStock">Generate</v-btn>
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            color="success"
            @click="downloadPDF"
            :disabled="!stockDetails.length"
          >
            Download PDF
          </v-btn>
        </v-col>
      </v-row>
      <div class="mt-3">
        <DataTableWithPagination
          v-if="stockDetails.length > 0"
          :headers="headers"
          :items="stockDetails"
          :loading="loading"
          :page="page"
          :total="total"
          @pageChange="onPageChange"
        >
          <template #row="{ item, index }">
            <tr
              :class="[
                index % 2 === 0 ? 'dark-row' : 'light-row',
                'custom-row',
              ]"
            >
              <td class="">{{ formatDate(item.created_at) }}</td>
              <td class="">{{ item.product_code }}</td>
              <td class="">{{ item.branch.name }}</td>
              <td class="">{{ item.transaction_type }}</td>
              <td class="">{{ item.type }}</td>
              <td class="">{{ item.quantity }}</td>
            </tr>
          </template>
        </DataTableWithPagination>
        <v-alert v-else type="info" class="mt-5" outlined>
          No records found.
        </v-alert>
      </div>
    </v-card>
  </div>
</template>

<script>
import { ReportService } from "@/services/report-service.js";
import { InvoiceService } from "@/services/invoice-service.js";
import { ProductService } from "@/services/product-service.js";
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  data() {
    return {
      filters: {
        branch_name: "",
        product_name: "",
        start_date: "",
        end_date: "",
      },
      startMenu: false,
      endMenu: false,
      headers: [
        { text: "Transaction Date", value: "created_at" },
        { text: "Product Code", value: "product_code" },
        { text: "Branch Name", value: "branch.name" },
        { text: "Transaction Type", value: "transaction_type" },
        { text: "Type", value: "type" },
        { text: "Quantity", value: "quantity" },
      ],
      stockDetails: [],
      loading: true,
      search: "",
      page: 1,
      total: 0,
      branches: [],
      products: [],
    };
  },
  components: {
    DataTableWithPagination,
  },
  created() {
    this.fetchBranches();
    this.fetchProducts();
    this.fetchStock();
  },
  watch: {
    "filters.branch_name"(val, oldVal) {
      if (val !== oldVal) this.fetchStock();
    },
    "filters.product_name"(val, oldVal) {
      if (val !== oldVal) this.fetchStock();
    },
    "filters.start_date"(val, oldVal) {
      if (val !== oldVal) this.fetchStock();
    },
    "filters.end_date"(val, oldVal) {
      if (val !== oldVal) this.fetchStock();
    },
    page() {
      this.fetchStock();
    },
  },
  methods: {
    async fetchStock() {
      this.loading = true;
      try {
        const response = await ReportService.getStockDetailsReport(
          {
            ...this.filters,
            search: this.search,
          },
          this.page
        );
        this.stockDetails = response.data?.data || [];
        this.total = response.data.meta.last_page || 0;
      } catch (error) {
        this.stockDetails = [];
        this.total = 0;
        console.error("Error fetching stock List:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchBranches() {
      try {
        const res = await InvoiceService.getBranches();
        this.branches = res.data || [];
      } catch (e) {
        this.branches = [];
      }
    },
    fetchProducts(search = "") {
      clearTimeout(this.productSearchTimeout);
      this.productSearchTimeout = setTimeout(async () => {
        try {
          const res = await ProductService.getProductListAll(search);
          this.products = res.data.data || [];
        } catch {
          this.products = [];
        }
      }, 400);
    },
    onPageChange(newPage) {
      this.page = newPage;
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    downloadPDF() {
      const doc = new jsPDF();

      // Main heading: larger and bold
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Stock Report", 14, 15);

      // Reset font for filters
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      // Prepare filter summary, each on its own line
      let y = 22;
      if (this.filters.branch_name) {
        doc.text(`Branch: ${this.filters.branch_name}`, 14, y);
        y += 7;
      }
      if (this.filters.product_name) {
        doc.text(`Product: ${this.filters.product_name}`, 14, y);
        y += 7;
      }
      if (this.filters.start_date) {
        doc.text(`Start Date: ${this.filters.start_date}`, 14, y);
        y += 7;
      }
      if (this.filters.end_date) {
        doc.text(`End Date: ${this.filters.end_date}`, 14, y);
        y += 7;
      }
      if (
        this.filters.branch_name ||
        this.filters.product_name ||
        this.filters.start_date ||
        this.filters.end_date
      ) {
        y += 2;
      }

      doc.autoTable({
        head: [this.headers.map((h) => h.text)],
        body: this.stockDetails.map((item) => [
          this.formatDate(item.created_at),
          item.product_code,
          item.branch?.name || "",
          item.transaction_type,
          item.type,
          item.quantity,
        ]),
        startY: y,
      });

      doc.save("stock_report.pdf");
    },
  },
};
</script>
